<template>
  <div class="bg-white shadow-lg rounded-xl border border-gray-200 p-6 mb-8">
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <!-- Search Section -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Search Products</label>
        <div class="relative">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name, SKU, category, metal, description, or labels"
            class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
            @input="handleSearch"
          />
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            v-if="searchQuery"
            @click="clearSearch"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Clear search"
          >
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Category Filter Section -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Category</label>
        <div class="relative">
          <select
            v-model="selectedCategory"
            @change="handleFilter"
            class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 appearance-none"
            aria-label="Select product category"
          >
            <option value="">All Categories</option>
            <option v-for="category in availableCategories" :key="category" :value="category">
              {{ category }} Collection
            </option>
          </select>
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Label Filters Section -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Labels</label>
        <div class="relative">
          <button
            @click="toggleLabelDropdown"
            class="w-full border-2 border-blue-300 rounded-lg py-3 pl-10 pr-8 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-left flex items-center justify-between"
          >
            <span class="text-gray-700">
              {{ selectedLabels.length > 0 ? `${selectedLabels.length} labels selected` : 'Select labels' }}
            </span>
          </button>
          <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg 
              :class="['h-5 w-5 text-gray-400 transition-transform', showLabelDropdown ? 'rotate-180' : '']" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          <!-- Label Dropdown -->
          <div 
            v-if="showLabelDropdown" 
            class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <div class="p-3">
              <div v-if="groupedLabels.category.length === 0 && groupedLabels.subcategory.length === 0 && groupedLabels.tag.length === 0" class="text-gray-500 text-sm py-2">
                No labels available
              </div>
              <div v-else class="space-y-4">
                <!-- Categories Section -->
                <div v-if="groupedLabels.category.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <div class="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Categories</h4>
                  </div>
                  <div class="space-y-1 ml-4">
                    <label 
                      v-for="label in groupedLabels.category" 
                      :key="label.id"
                      class="flex items-center space-x-2 cursor-pointer hover:bg-orange-50 p-2 rounded transition-colors"
                    >
                      <input
                        :id="`label-${label.id}`"
                        v-model="selectedLabels"
                        :value="label.id"
                        type="checkbox"
                        class="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        @change="handleLabelChange"
                      />
                      <span class="text-sm font-medium px-3 py-1 rounded bg-orange-100 text-orange-700">
                        {{ label.name }}
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Subcategories Section -->
                <div v-if="groupedLabels.subcategory.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Subcategories</h4>
                  </div>
                  <div class="space-y-1 ml-4">
                    <label 
                      v-for="label in groupedLabels.subcategory" 
                      :key="label.id"
                      class="flex items-center space-x-2 cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors"
                    >
                      <input
                        :id="`label-${label.id}`"
                        v-model="selectedLabels"
                        :value="label.id"
                        type="checkbox"
                        class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        @change="handleLabelChange"
                      />
                      <span class="text-sm font-medium px-3 py-1 rounded bg-blue-100 text-blue-700">
                        {{ label.name }}
                      </span>
                    </label>
                  </div>
                </div>

                <!-- Tags Section -->
                <div v-if="groupedLabels.tag.length > 0">
                  <div class="flex items-center space-x-2 mb-2">
                    <div class="w-2 h-2 bg-amber-500 rounded-full"></div>
                    <h4 class="text-sm font-semibold text-gray-700 uppercase tracking-wide">Tags</h4>
                  </div>
                  <div class="space-y-1 ml-4">
                    <label 
                      v-for="label in groupedLabels.tag" 
                      :key="label.id"
                      class="flex items-center space-x-2 cursor-pointer hover:bg-amber-50 p-2 rounded transition-colors"
                    >
                      <input
                        :id="`label-${label.id}`"
                        v-model="selectedLabels"
                        :value="label.id"
                        type="checkbox"
                        class="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                        @change="handleLabelChange"
                      />
                      <span class="text-sm font-medium px-3 py-1 rounded bg-amber-100 text-amber-700">
                        {{ label.name }}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
              <div v-if="selectedLabels.length > 0" class="mt-3 pt-3 border-t border-gray-200">
                <button
                  @click="clearLabels"
                  class="text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  Clear all labels
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Filters Display -->
    <div v-if="hasActiveFilters" class="mt-4 pt-4 border-t border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <span class="text-sm font-medium text-gray-700">Active filters:</span>
          <div class="flex flex-wrap gap-2">
            <span v-if="searchQuery" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
              Search: "{{ searchQuery }}"
              <button @click="clearSearch" class="ml-2 text-orange-600 hover:text-orange-800">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
            <span v-if="selectedCategory" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Category: {{ selectedCategory }}
              <button @click="clearCategory" class="ml-2 text-blue-600 hover:text-blue-800">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
            <span v-if="selectedLabels.length > 0" class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Labels: {{ selectedLabels.length }} selected
              <button @click="clearLabels" class="ml-2 text-amber-600 hover:text-amber-800">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>
        </div>
        <button 
          @click="clearAllFilters"
          class="text-sm text-gray-600 hover:text-gray-800 font-medium"
        >
          Clear all filters
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useLabelsStore } from '../stores/labels'
import { useFiltersStore } from '../stores/filters'

export default {
  name: 'GlobalFilters',
  emits: ['filter-change'],
  setup(props, { emit }) {
    const labelsStore = useLabelsStore()
    const filtersStore = useFiltersStore()
    
    // State
    const showLabelDropdown = ref(false)
    const availableLabels = ref([])
    const groupedLabels = ref({ category: [], subcategory: [], tag: [] })
    const availableCategories = ref([])

    // Use store values
    const searchQuery = computed({
      get: () => filtersStore.searchQuery,
      set: (value) => filtersStore.setSearch(value)
    })

    const selectedCategory = computed({
      get: () => filtersStore.selectedCategory,
      set: (value) => filtersStore.setCategory(value)
    })

    const selectedLabels = computed({
      get: () => filtersStore.selectedLabels,
      set: (value) => filtersStore.setLabels(value)
    })

    const hasActiveFilters = computed(() => filtersStore.hasActiveFilters)

    // Methods
    const loadCategories = async () => {
      try {
        const response = await fetch('/api/products/categories/list')
        const data = await response.json()
        availableCategories.value = data.categories || []
      } catch (error) {
        console.error('Failed to load categories:', error)
        availableCategories.value = []
      }
    }

    const loadLabels = async () => {
      try {
        await labelsStore.fetchLabelsGrouped()
        availableLabels.value = labelsStore.labels
        groupedLabels.value = labelsStore.groupedLabels
      } catch (error) {
        console.error('Failed to load labels:', error)
        availableLabels.value = []
        groupedLabels.value = { category: [], subcategory: [], tag: [] }
      }
    }

    const handleSearch = () => {
      emitFilterChange()
    }

    const handleFilter = () => {
      emitFilterChange()
    }

    const handleLabelChange = () => {
      emitFilterChange()
    }

    const emitFilterChange = () => {
      emit('filter-change', {
        search: searchQuery.value,
        category: selectedCategory.value,
        labels: selectedLabels.value
      })
    }

    const clearSearch = () => {
      filtersStore.clearSearch()
      emitFilterChange()
    }

    const clearCategory = () => {
      filtersStore.clearCategory()
      emitFilterChange()
    }

    const clearLabels = () => {
      filtersStore.clearLabels()
      emitFilterChange()
    }

    const clearAllFilters = () => {
      filtersStore.clearAllFilters()
      emitFilterChange()
    }

    const toggleLabelDropdown = () => {
      showLabelDropdown.value = !showLabelDropdown.value
    }

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        showLabelDropdown.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadCategories()
      loadLabels()
      document.addEventListener('click', handleClickOutside)
    })

    // Watch for external changes to filters (e.g., from other components)
    watch(() => labelsStore.filterLabelIds, (newLabels) => {
      filtersStore.setLabels(newLabels)
    }, { deep: true })

    return {
      searchQuery,
      selectedCategory,
      selectedLabels,
      showLabelDropdown,
      availableLabels,
      groupedLabels,
      availableCategories,
      hasActiveFilters,
      handleSearch,
      handleFilter,
      handleLabelChange,
      clearSearch,
      clearCategory,
      clearLabels,
      clearAllFilters,
      toggleLabelDropdown
    }
  }
}
</script>
